import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  AlertCircle,
  XCircle,
  Package,
  Settings,
  RefreshCw,
  ArrowRight,
  Save,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useGetStockSummaryQuery,
  useGetLowStockProductsQuery,
  useGetOutOfStockProductsQuery,
  useGetStockThresholdsQuery,
  useUpdateStockThresholdsMutation,
  useUpdateProductStockMutation,
  type ProductStockAlert,
  type StockAlertLevel,
} from "@/store";
import { TablePagination, type PaginationInfo } from "@/components/admin";
import { toast } from "sonner";

const ALERT_CONFIG: Record<
  StockAlertLevel,
  { label: string; color: string; bgColor: string; icon: React.ReactNode }
> = {
  OUT_OF_STOCK: {
    label: "Out of Stock",
    color: "text-red-600 dark:text-red-400",
    bgColor: "bg-red-100 dark:bg-red-900/20",
    icon: <XCircle className="h-4 w-4" />,
  },
  CRITICAL: {
    label: "Critical",
    color: "text-orange-600 dark:text-orange-400",
    bgColor: "bg-orange-100 dark:bg-orange-900/20",
    icon: <AlertCircle className="h-4 w-4" />,
  },
  LOW: {
    label: "Low",
    color: "text-yellow-600 dark:text-yellow-400",
    bgColor: "bg-yellow-100 dark:bg-yellow-900/20",
    icon: <AlertTriangle className="h-4 w-4" />,
  },
  NORMAL: {
    label: "Normal",
    color: "text-green-600 dark:text-green-400",
    bgColor: "bg-green-100 dark:bg-green-900/20",
    icon: <Package className="h-4 w-4" />,
  },
};

function StockAlertBadge({ level }: { level: StockAlertLevel }) {
  const config = ALERT_CONFIG[level];
  return (
    <Badge className={`${config.bgColor} ${config.color} border-0 gap-1`}>
      {config.icon}
      {config.label}
    </Badge>
  );
}

interface QuickStockUpdateProps {
  product: ProductStockAlert;
  onUpdate: (id: string, quantity: number) => void;
  isUpdating: boolean;
}

function QuickStockUpdate({ product, onUpdate, isUpdating }: QuickStockUpdateProps) {
  const [quantity, setQuantity] = useState(product.stockQuantity.toString());
  const [isEditing, setIsEditing] = useState(false);

  const handleSave = () => {
    const newQuantity = parseInt(quantity);
    if (isNaN(newQuantity) || newQuantity < 0) {
      toast.error("Please enter a valid non-negative number");
      return;
    }
    onUpdate(product.id, newQuantity);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          className="w-20 h-8"
          min="0"
        />
        <Button size="sm" onClick={handleSave} disabled={isUpdating}>
          <Save className="h-3 w-3" />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setIsEditing(true)}
      className="text-left hover:underline"
    >
      {product.stockQuantity}
    </button>
  );
}

export function StockManagementScreen() {
  const [activeTab, setActiveTab] = useState("alerts");
  const [lowStockPage, setLowStockPage] = useState(1);
  const [outOfStockPage, setOutOfStockPage] = useState(1);
  const [lowStockLimit, setLowStockLimit] = useState(10);
  const [outOfStockLimit, setOutOfStockLimit] = useState(10);

  // Threshold form state
  const [lowThreshold, setLowThreshold] = useState("");
  const [criticalThreshold, setCriticalThreshold] = useState("");

  // API queries
  const { data: summaryData, isLoading: summaryLoading, refetch: refetchSummary } = useGetStockSummaryQuery();
  const { data: lowStockData, isLoading: lowStockLoading, refetch: refetchLowStock } = useGetLowStockProductsQuery({
    page: lowStockPage,
    limit: lowStockLimit,
  });
  const { data: outOfStockData, isLoading: outOfStockLoading, refetch: refetchOutOfStock } = useGetOutOfStockProductsQuery({
    page: outOfStockPage,
    limit: outOfStockLimit,
  });
  const { data: thresholdsData, isLoading: thresholdsLoading } = useGetStockThresholdsQuery();
  const [updateThresholds, { isLoading: isUpdatingThresholds }] = useUpdateStockThresholdsMutation();
  const [updateStock, { isLoading: isUpdatingStock }] = useUpdateProductStockMutation();

  const summary = summaryData?.data;
  const thresholds = thresholdsData?.data;
  const lowStockProducts = lowStockData?.data || [];
  const outOfStockProducts = outOfStockData?.data || [];

  const lowStockPagination: PaginationInfo = lowStockData?.pagination || {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  };

  const outOfStockPagination: PaginationInfo = outOfStockData?.pagination || {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  };

  // Set threshold form values when data loads
  useState(() => {
    if (thresholds && !lowThreshold && !criticalThreshold) {
      setLowThreshold(thresholds.lowStockThreshold.toString());
      setCriticalThreshold(thresholds.criticalStockThreshold.toString());
    }
  });

  const handleRefresh = () => {
    refetchSummary();
    refetchLowStock();
    refetchOutOfStock();
    toast.success("Stock data refreshed");
  };

  const handleUpdateThresholds = async () => {
    const low = parseInt(lowThreshold);
    const critical = parseInt(criticalThreshold);

    if (isNaN(low) || isNaN(critical)) {
      toast.error("Please enter valid numbers");
      return;
    }

    if (critical >= low) {
      toast.error("Critical threshold must be less than low stock threshold");
      return;
    }

    if (low < 0 || critical < 0) {
      toast.error("Thresholds must be non-negative");
      return;
    }

    try {
      await updateThresholds({
        lowStockThreshold: low,
        criticalStockThreshold: critical,
      }).unwrap();
      toast.success("Thresholds updated successfully");
    } catch (error: any) {
      toast.error(error?.data?.error || "Failed to update thresholds");
    }
  };

  const handleUpdateProductStock = async (productId: string, quantity: number) => {
    try {
      await updateStock({ id: productId, stockQuantity: quantity }).unwrap();
      toast.success("Stock updated successfully");
    } catch (error: any) {
      toast.error(error?.data?.error || "Failed to update stock");
    }
  };

  const renderProductTable = (products: ProductStockAlert[], isLoading: boolean) => {
    if (isLoading) {
      return (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-12 w-12 rounded-md" />
              <div className="flex-1">
                <Skeleton className="h-4 w-32 mb-1" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-6 w-20" />
            </div>
          ))}
        </div>
      );
    }

    if (products.length === 0) {
      return (
        <div className="text-center py-8">
          <Package className="h-12 w-12 mx-auto text-green-500 mb-3" />
          <p className="text-muted-foreground">No products in this category</p>
        </div>
      );
    }

    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[60px]">Image</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id}>
                <TableCell>
                  <div className="h-10 w-10 rounded-md overflow-hidden bg-muted">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <p className="font-medium text-sm">{product.name}</p>
                </TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">
                    {product.category?.name || "Uncategorized"}
                  </span>
                </TableCell>
                <TableCell>
                  <QuickStockUpdate
                    product={product}
                    onUpdate={handleUpdateProductStock}
                    isUpdating={isUpdatingStock}
                  />
                </TableCell>
                <TableCell>
                  <StockAlertBadge level={product.alertLevel} />
                </TableCell>
                <TableCell className="text-right">
                  <Link to={`/admin/products/${product.id}/edit`}>
                    <Button variant="ghost" size="sm">
                      Edit Product
                      <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Stock Management</h1>
          <p className="text-muted-foreground">
            Monitor and manage your inventory levels
          </p>
        </div>
        <Button onClick={handleRefresh} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {summaryLoading ? (
          <>
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-24 rounded-lg" />
            ))}
          </>
        ) : (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Products</CardTitle>
                <Package className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary?.totalProducts || 0}</div>
                <p className="text-xs text-muted-foreground">In catalog</p>
              </CardContent>
            </Card>
            <Card className="border-red-200 dark:border-red-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Out of Stock</CardTitle>
                <XCircle className="h-4 w-4 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {summary?.outOfStock || 0}
                </div>
                <p className="text-xs text-muted-foreground">Needs restocking</p>
              </CardContent>
            </Card>
            <Card className="border-orange-200 dark:border-orange-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Critical Stock</CardTitle>
                <AlertCircle className="h-4 w-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {summary?.criticalStock || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  Below {thresholds?.criticalStockThreshold || 5} units
                </p>
              </CardContent>
            </Card>
            <Card className="border-yellow-200 dark:border-yellow-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Low Stock</CardTitle>
                <AlertTriangle className="h-4 w-4 text-yellow-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {summary?.lowStock || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  Below {thresholds?.lowStockThreshold || 10} units
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Tabs for different views */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="alerts" className="gap-2">
            <AlertTriangle className="h-4 w-4" />
            Stock Alerts
            {(summary?.outOfStock || 0) + (summary?.criticalStock || 0) + (summary?.lowStock || 0) > 0 && (
              <Badge variant="secondary" className="ml-1">
                {(summary?.outOfStock || 0) + (summary?.criticalStock || 0) + (summary?.lowStock || 0)}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="out-of-stock" className="gap-2">
            <XCircle className="h-4 w-4" />
            Out of Stock
            {(summary?.outOfStock || 0) > 0 && (
              <Badge variant="destructive" className="ml-1">{summary?.outOfStock}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-2">
            <Settings className="h-4 w-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Low Stock Products</CardTitle>
              <CardDescription>
                Products below the low stock threshold ({thresholds?.lowStockThreshold || 10} units)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {renderProductTable(lowStockProducts, lowStockLoading)}
              {!lowStockLoading && lowStockProducts.length > 0 && (
                <TablePagination
                  pagination={lowStockPagination}
                  onPageChange={setLowStockPage}
                  onLimitChange={(newLimit) => {
                    setLowStockLimit(newLimit);
                    setLowStockPage(1);
                  }}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="out-of-stock" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Out of Stock Products</CardTitle>
              <CardDescription>
                Products that are currently unavailable and need restocking
              </CardDescription>
            </CardHeader>
            <CardContent>
              {renderProductTable(outOfStockProducts, outOfStockLoading)}
              {!outOfStockLoading && outOfStockProducts.length > 0 && (
                <TablePagination
                  pagination={outOfStockPagination}
                  onPageChange={setOutOfStockPage}
                  onLimitChange={(newLimit) => {
                    setOutOfStockLimit(newLimit);
                    setOutOfStockPage(1);
                  }}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Stock Alert Thresholds</CardTitle>
              <CardDescription>
                Configure when products should trigger low stock and critical stock alerts
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {thresholdsLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : (
                <>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="lowThreshold" className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-yellow-500" />
                        Low Stock Threshold
                      </Label>
                      <Input
                        id="lowThreshold"
                        type="number"
                        value={lowThreshold || thresholds?.lowStockThreshold || ""}
                        onChange={(e) => setLowThreshold(e.target.value)}
                        min="0"
                        placeholder="e.g., 10"
                      />
                      <p className="text-xs text-muted-foreground">
                        Products below this quantity will show as "Low Stock"
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="criticalThreshold" className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-orange-500" />
                        Critical Stock Threshold
                      </Label>
                      <Input
                        id="criticalThreshold"
                        type="number"
                        value={criticalThreshold || thresholds?.criticalStockThreshold || ""}
                        onChange={(e) => setCriticalThreshold(e.target.value)}
                        min="0"
                        placeholder="e.g., 5"
                      />
                      <p className="text-xs text-muted-foreground">
                        Products below this quantity will show as "Critical" (must be less than low stock threshold)
                      </p>
                    </div>
                  </div>
                  <Button onClick={handleUpdateThresholds} disabled={isUpdatingThresholds}>
                    {isUpdatingThresholds ? "Saving..." : "Save Thresholds"}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Current Settings</CardTitle>
              <CardDescription>
                How stock levels are currently classified
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/10">
                  <XCircle className="h-5 w-5 text-red-500" />
                  <div>
                    <p className="font-medium">Out of Stock</p>
                    <p className="text-sm text-muted-foreground">Stock quantity is 0 or marked as not in stock</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-50 dark:bg-orange-900/10">
                  <AlertCircle className="h-5 w-5 text-orange-500" />
                  <div>
                    <p className="font-medium">Critical Stock</p>
                    <p className="text-sm text-muted-foreground">
                      Stock quantity is {thresholds?.criticalStockThreshold || 5} or below
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/10">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                  <div>
                    <p className="font-medium">Low Stock</p>
                    <p className="text-sm text-muted-foreground">
                      Stock quantity is between {(thresholds?.criticalStockThreshold || 5) + 1} and {thresholds?.lowStockThreshold || 10}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/10">
                  <Package className="h-5 w-5 text-green-500" />
                  <div>
                    <p className="font-medium">Normal Stock</p>
                    <p className="text-sm text-muted-foreground">
                      Stock quantity is above {thresholds?.lowStockThreshold || 10}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
